import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAllAssignmentQuestionQuery,
  useGetAssignmentSubmitQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import TestScreenQuestionCard from "../Components/TestScreenQuestionCard.jsx";
import { Button } from "flowbite-react";
import { IoMdArrowRoundBack } from "react-icons/io";
import TestLayout from "../Layout/TestLayout.jsx";
import NavBar from "../../../Common/NavBar";

const AssignmentTestScreen = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignmentQuestion, setAssignmentQuestion] = useState([]);
  const [fullScreen, setFullScreen] = useState(document.fullscreenElement);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const submitRef = useRef();
  const [assignmentSubmitted, setAssignmentSubmitted] = useState(false); // kept for future use if needed
  
  // Check if user already completed the warning/agreement flow for this assignment
  const warningCompletedKey = `warningCompleted_${assignmentId}`;
  const hasCompletedWarning = sessionStorage.getItem(warningCompletedKey) === 'true';
  
  // Modal states - initialize based on whether user already completed the flow
  const [showWarning, setShowWarning] = useState(!hasCompletedWarning);
  const [showAgreement, setShowAgreement] = useState(false);
  const [consentInput, setConsentInput] = useState("");
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [showDndGuide, setShowDndGuide] = useState(false);

  const CONSENT_PHRASE = "I TURNED OFF NOTIFICATION";
  const consentMatches = consentInput.trim().toUpperCase() === CONSENT_PHRASE;
  const consentProgress = Math.min(
    100,
    Math.round(
      (consentInput.trim().toUpperCase().length / CONSENT_PHRASE.length) * 100
    ) || 0
  );

  const studentEmail = JSON.parse(localStorage.getItem("user"))?.email;

  const {
    data: student,
    loading: studentLoading,
    error: studentError,
  } = useGetStudentByEmailIdQuery({
    variables: { email: studentEmail },
  });

  const {
    data: AssignmentQuestions,
    loading: questionsLoading,
    error: questionsError,
  } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId },
    fetchPolicy: "network-only",
  });

  const { data: campus } = useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId?.campusId,
  });

  const {
    data: AssignmentCompleted,
    loading: submitLoading,
    error: submitError,
    refetch,
  } = useGetAssignmentSubmitQuery({
    variables: {
      assignmentId,
      studentId: student?.getStudentByEmailId?.id,
      campus_name: campus?.campus?.name,
    },
    skip: !student?.getStudentByEmailId || !campus?.campus,
    fetchPolicy: "network-only",
  });

  

  useEffect(() => {
    if (AssignmentQuestions) {
      setAssignmentQuestion(AssignmentQuestions.getAllAssignmentQuestion);
    }
    refetch();
  }, [AssignmentQuestions]);

  const toggleFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable full-screen mode:", err);
      });
    }
  };

  const handleFullScreenChange = () => {
    setFullScreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (!document.fullscreenElement) {
      toggleFullScreen();
    }
  }, []);

  useEffect(() => {
    if (studentError || questionsError || submitError) {
      setShowErrorPage(true);
    }
  }, [studentError, questionsError, submitError]);

  useEffect(() => {
    const holdRedirect = sessionStorage.getItem("bt_submit_ui_pending") === "1";
    if (AssignmentCompleted?.getAssignmentSubmit?.completed && !holdRedirect) {
      navigate(`/assignments/${assignmentId}/summary`, {
        replace: true,
        state: { submission: AssignmentCompleted.getAssignmentSubmit },
      });
    }
  }, [AssignmentCompleted, assignmentId, navigate]);

  // Block browser back/route change
  useEffect(() => {
    const handlePopState = (e) => {
      if (!assignmentSubmitted) {
        e.preventDefault();
        if (submitRef.current) submitRef.current.triggerLeave();
        window.history.pushState(null, '', window.location.href); // Prevent back
      }
    };
    window.history.pushState(null, '', window.location.href); // Set initial state
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [assignmentSubmitted]);

  const handleTryAgain = () => {
    setShowErrorPage(false);
    window.location.reload();
  };

  // 👉 Render full error page if error exists
  if (showErrorPage) {
    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen p-6 text-center bg-gray-100 font-poppins">
        <img src="https://cdn-icons-png.flaticon.com/512/13983/13983169.png" alt="Error" className="w-40 h-40 mb-4" />
        <h2 className="mb-2 text-xl font-semibold text-gray-800">
          Something went wrong
        </h2>
        <p className="mb-6 text-gray-600">
          We couldn't load the assignment. Please try again.
        </p>
        <Button onClick={handleTryAgain} className="text-white px-4 py-0.5 rounded-md">
          Click Here...
        </Button>
      </div>
    );
  }

  // While custom submit UI is pending, don't auto-redirect yet.
  const holdRedirect = sessionStorage.getItem("bt_submit_ui_pending") === "1";
  if (AssignmentCompleted?.getAssignmentSubmit?.completed && !holdRedirect) {
    return null;
  }

  const autoSubmitRules = [
    { text: "You leave or close the test window." },
    { text: "You switch tabs during the test." },
    { text: <>Your system date/time is not set to <strong>live IST timezone</strong>.</> },
    { text: "Your screen turns off or the system shuts down." },
    { text: <><strong>Antivirus or security notifications appear during the test.</strong></> },
    { text: "You enable touchpad gestures or the on-screen keyboard." },
    { text: "The due date has passed." },
    { text: "Changing Wi-Fi networks or internet connections during the assessment is strictly prohibited." },
    { text: "Clicking PrintScreen, Windows key, or Inspect Element is not allowed." },
    { text: <><strong>Use a stable internet connection before starting the assessment.</strong></> },
   
  ];

  const pledgeItems = [
    "I will not copy code from others or any external source.",
    "I understand this assessment measures my actual skill level.",
    "I know honest effort matters more than marks for long-term growth.",
    "I will attempt every question independently and in good faith.",
  ];

  // Shared step progress bar
  const StepBar = ({ step }) => (
    <div className="flex justify-center mb-2">
      <div className="flex items-center gap-0 w-52">
        {["Instructions", "Confirmation"].map((label, i) => {
          const s = i + 1;
          const active = s === step;
          const done = s < step;
          return (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                    done
                      ? "border-[#0E3D5D] text-white"
                      : active
                      ? "bg-white border-[#0E3D5D] text-[#0E3D5D]"
                      : "bg-white border-gray-300 text-gray-400"
                  }`}
                  style={done ? { backgroundColor: "#0E3D5D" } : {}}
                >
                  {done ? (
                    <svg viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4">
                      <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                    </svg>
                  ) : s}
                </div>
                <span className={`text-xs font-medium ${active ? "text-[#0E3D5D]" : "text-gray-400"}`}>
                  {label}
                </span>
              </div>
              {i < 1 && (
                <div className={`flex-1 h-px mb-4 mx-2 ${done ? "bg-[#0E3D5D]" : "bg-gray-200"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  // Shared modal shell
  const ModalShell = ({ children }) => (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-white px-3 pb-3 pt-16 sm:items-center sm:p-4 sm:pt-20">
      <NavBar />
      <div
        className="flex flex-col w-full max-w-4xl my-3 overflow-hidden bg-white border border-gray-200 shadow-xl rounded-xl sm:my-0"
        style={{ animation: "scaleIn 200ms cubic-bezier(0.34,1.56,0.64,1)", fontFamily: "'Poppins', sans-serif", maxHeight: "92vh" }}
      >
        {children}
      </div>
    </div>
  );

  // 👉 First Modal - Warning
  if (showWarning && !AssignmentCompleted?.getAssignmentSubmit?.completed) {
    return (
      <ModalShell>
        <div className="flex flex-col flex-1 min-h-0 gap-3 px-4 pt-4 pb-3 overflow-y-auto sm:px-6">
          <StepBar step={1} />

          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-600">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Important — Read Before You Start</h2>
              <h3 className="text-sm text-gray-500 mt-0.5"><strong> Your test will be auto-submitted if any of the following occur.</strong> </h3>
            </div>
          </div>

          {/* Rules list */}
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            {autoSubmitRules.map((rule, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2 bg-white">
                <svg viewBox="0 0 16 16" fill="currentColor" className="mt-1 h-3.5 w-3.5 shrink-0 text-red-400">
                  <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5A5.5 5.5 0 118 2.5a5.5 5.5 0 010 11zM7.25 5.75a.75.75 0 011.5 0v3a.75.75 0 01-1.5 0v-3zm.75 5.5a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                </svg>
                <p className="text-sm leading-relaxed text-gray-700 sm:text-[15px]">{rule.text}</p>
              </div>
            ))}
          </div>

          {/* DnD accordion */}
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowDndGuide((v) => !v)}
              className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-left text-gray-700 transition-colors bg-white hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                </svg>
                How to enable Do Not Disturb / Focus mode
              </span>
              <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-gray-400 transition-transform ${showDndGuide ? "rotate-180" : ""}`}>
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            {showDndGuide && (
              <div className="grid gap-4 px-4 py-3 text-sm text-gray-600 bg-white border-t border-gray-100 sm:grid-cols-2">
                <div>
                  <p className="mb-1 font-semibold text-gray-800">macOS</p>
                  <ol className="ml-4 space-y-1 leading-relaxed list-decimal">
                    <li>Open Control Center → Focus</li>
                    <li>Select Do Not Disturb</li>
                  </ol>
                </div>
                <div>
                  <p className="mb-1 font-semibold text-gray-800">Windows 10 / 11</p>
                  <ol className="ml-4 space-y-1 leading-relaxed list-decimal">
                    <li>Click Notification icon (system tray)</li>
                    <li>Enable Focus Assist → Alarms only</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* Note */}
          <div className="flex items-center gap-2.5 px-4 py-2 rounded-lg bg-white border border-gray-200">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-500 shrink-0">
              <path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-600">Ensure all conditions above are met before you click <strong>Next</strong>.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2.5 border-t border-gray-200 bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span className="text-sm text-gray-400 sm:order-1">Step 1 of 2</span>
          <button
            onClick={() => { setShowWarning(false); setShowAgreement(true); }}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-base font-semibold text-white transition-colors sm:order-2 sm:w-auto"
            style={{ backgroundColor: "#0E3D5D" }}
          >
            Next
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </ModalShell>
    );
  }

  // 👉 Second Modal - Consent
  if (showAgreement && !AssignmentCompleted?.getAssignmentSubmit?.completed) {
    return (
      <ModalShell>
        <div className="flex flex-col flex-1 min-h-0 gap-3 px-4 pt-4 pb-3 overflow-y-auto sm:px-6">
          <StepBar step={2} />

          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-700">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Academic Integrity Agreement</h2>
              <p className="text-sm text-gray-500 mt-0.5">Read and confirm your commitment before starting.</p>
            </div>
          </div>

          {/* Notification warning */}
         

          {/* Pledge list with I Agree checkbox */}
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            {pledgeItems.map((item, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-1.5 bg-white">
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-500">
                  <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                </svg>
                <p className="text-base leading-snug text-gray-700">{item}</p>
              </div>
            ))}
            <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="agreeCheckbox"
                  checked={agreeChecked}
                  onChange={(e) => setAgreeChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-[#0E3D5D] border-gray-300 rounded focus:ring-[#0E3D5D] focus:ring-2"
                />
                <span className="text-sm leading-relaxed text-gray-700">
                  I agree to the above terms and conditions
                </span>
              </label>
            </div>
          </div>

          {/* Type-to-confirm */}
          <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg">
             <div className="flex items-start gap-3 px-3.5 py-2.5 mb-5 rounded-lg bg-amber-50 border border-amber-200">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mt-0.5 shrink-0 text-amber-500">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            <p className="text-sm leading-snug text-amber-800">
              Any <strong>notification pop-up</strong> during the test is treated as opening a new tab and will <strong>auto-submit</strong> your test. Disable notifications before starting.
            </p>
          </div>
            <p className="text-sm text-gray-600 mb-1.5 leading-relaxed">
              Type{" "}
              <code className="px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-800 font-mono text-xs font-semibold tracking-widest">
                I TURNED OFF NOTIFICATION
              </code>{" "}
              below to confirm you have read and accepted the terms above.
            </p>
            <input
              type="text"
              value={consentInput}
              onChange={(e) => setConsentInput(e.target.value)}
              placeholder="I TURNED OFF NOTIFICATION"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className={`w-full px-3.5 py-2.5 text-base rounded-lg border outline-none font-mono tracking-widest transition-all duration-150 ${
                consentMatches
                  ? "border-gray-800 bg-white text-gray-900 ring-2 ring-gray-200"
                  : "border-gray-300 bg-white text-gray-800 focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
              }`}
            />
            <div className="mt-2">
              <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full transition-all duration-300 rounded-full"
                  style={{
                    width: `${consentProgress}%`,
                    background: consentMatches ? "#0E3D5D" : "#94a3b8",
                  }}
                />
              </div>
            </div>
            <div className="h-4 mt-1">
              {consentMatches && (
                <p className="text-sm text-gray-600 flex items-center gap-1.5 font-medium">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-700">
                    <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                  </svg>
                  Confirmed. You may now start the test.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2.5 border-t border-gray-200 bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <button
            onClick={() => { setShowAgreement(false); setShowWarning(true); setConsentInput(""); setAgreeChecked(false); }}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-base font-medium text-gray-600 transition-colors hover:bg-gray-100 sm:w-auto"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M9.78 12.78a.75.75 0 01-1.06 0L4.47 8.53a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 1.06L6.06 8l3.72 3.72a.75.75 0 010 1.06z" clipRule="evenodd" />
            </svg>
            Back
          </button>
          <span className="text-sm text-gray-400 sm:order-1">Step 2 of 2</span>
          <button
            disabled={!consentMatches || !agreeChecked}
            onClick={() => {
              setShowAgreement(false);
              sessionStorage.setItem(warningCompletedKey, "true");
            }}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-base font-semibold transition-all duration-150 sm:w-auto ${
              consentMatches && agreeChecked
                ? "text-white"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            style={consentMatches && agreeChecked ? { backgroundColor: "#0E3D5D" } : {}}
          >
            Start Test
          </button>
        </div>
      </ModalShell>
    );
  }

  // 👉 Fullscreen check (shown after modals)
  if (!fullScreen) {
    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen p-4 bg-white">
        <h1 className="mb-4 text-lg text-center md:text-xl">Please switch to Fullscreen to continue</h1>
        <Button className="bg-primary" onClick={toggleFullScreen}>
          Full Screen
        </Button>
      </div>
    );
  }

  // 👉 Main assignment test screen
  return (
    <TestLayout
      assignmentId={assignmentId}
      submitRef={submitRef}
      topBarLeft={
        <div className="flex items-start gap-3 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
          <div className="flex items-center justify-center text-indigo-600 h-11 w-11 rounded-2xl bg-indigo-50">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 md:text-2xl">Assignment Test</h1>
            <p className="text-sm text-gray-500">Solve each question independently. Click a card to begin.</p>
          </div>
        </div>
      }
    >
      <div className="w-full px-6 pb-10 md:px-12" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="w-full mx-auto max-w-none">
          <div className="flex items-center gap-4 mt-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Questions</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="flex flex-col gap-4 mt-6">
            {assignmentQuestion?.map((question, index) => (
              <div key={question.id || index}>
                <TestScreenQuestionCard
                  data={question}
                  index={index + 1}
                  completedQuestions={AssignmentCompleted?.getAssignmentSubmit?.questions}
                  refetch={refetch}
                />
              </div>
            ))}
          </div>

          {/* Submit Test button below questions */}
          <div className="flex justify-end mt-8">
            <Button
              className="flex items-center p-0 text-center bg-red-700 cursor-pointer hover:bg-red-800 focus:outline-none focus:ring-0"
              onClick={() => submitRef.current && submitRef.current.triggerSubmit()}
              style={{ backgroundColor: '#b91c1c' }}
            >
              End Test
            </Button>
          </div>
        </div>
      </div>
    </TestLayout>
  );
};

export default AssignmentTestScreen;